// The client-side rules for custom field values, shared by the record page (CustomFieldValueInput) and
// the Opportunities list's inline editors, so the two surfaces cannot accept different
// input. custom-field-service's validateCustomFieldValues is still the final authority; a value it
// rejects is a failed save, not something these rules have to anticipate.
import moment from 'moment';
import {profileLocales} from '../../../constants';
import {getCurrencyByCode, isPrefixed} from '../../../data/currencyHelpers';

export const isValidInteger = (val: string | null): boolean => {
	if (val === null || val === '') return true;
	return /^-?\d+$/.test(val);
};

export const integerConverterValue = (value: string | number | null) => {
	if (value === null || value === undefined || value === '') {
		return {float: null, full: '', error: false};
	}
	const str = String(value).trim();
	if (/^-?\d+$/.test(str)) {
		return {float: parseInt(str, 10), full: str, error: false};
	}
	return {float: null, full: str, error: false};
};

export const filterIntegerInput = (val: string): string => {
	const stripped = val.replace(/[^0-9-]/g, '');
	if (stripped === '-') return '-';
	const negative = stripped.startsWith('-');
	const digits = stripped.replace(/-/g, '');
	return (negative ? '-' : '') + digits;
};

// The decimal separator a viewer types and reads, from their profile language. Unknown or missing
// languages use '.'.
export const decimalSeparatorFor = (language: string | null | undefined): ',' | '.' =>
	profileLocales[language as keyof typeof profileLocales]?.usesCommaDecimal ? ',' : '.';

// The decimal separator an amount reads with for a formatting locale (react-intl's locale): the one the
// browser's number format for that locale writes, as Forecast's other money on the page is written. It can
// differ from the separator the viewer's Decimal fields use: an English-EU viewer types decimals with a comma
// but reads money as 1,250.50. A missing or unknown locale uses '.'.
export const amountDecimalSeparatorFor = (locale: string | null | undefined): ',' | '.' => {
	if (!locale) return '.';
	try {
		// en-US comes after the locale, so a locale the browser does not know reads as en-US, not as the
		// browser's own default.
		const decimal = new Intl.NumberFormat([locale, 'en-US']).formatToParts(1.5).find(part => part.type === 'decimal');
		return decimal?.value === ',' ? ',' : '.';
	} catch {
		return '.';
	}
};

// An amount has at most this many digits before its decimal point and this many after it: Forecast keeps
// money as DECIMAL(11,2), so the largest amount is 999,999,999.99, and every currency has 2 decimals. The
// service applies the same limits.
export const AMOUNT_MAX_INTEGER_DIGITS = 9;
export const AMOUNT_DECIMALS = 2;

export type CurrencyEntry = {symbol: string; prefixed: boolean; decimals: number};

// How an amount in a currency is typed and read: Forecast's symbol for it, whether that goes before the
// amount, and how many decimals it has (always AMOUNT_DECIMALS). The symbol and its place come from
// Forecast's currency catalog, so every viewer sees the same, as elsewhere in Forecast. A code the catalog
// does not list shows as itself, before the amount.
export const currencyEntry = (currencyCode: string | null | undefined): CurrencyEntry => {
	const currency = getCurrencyByCode(currencyCode);
	return currency
		? {symbol: currency.symbol, prefixed: isPrefixed(currency), decimals: AMOUNT_DECIMALS}
		: {symbol: currencyCode ?? '', prefixed: true, decimals: AMOUNT_DECIMALS};
};

const AMOUNT = new RegExp(`^-?\\d{1,${AMOUNT_MAX_INTEGER_DIGITS}}(\\.\\d{1,${AMOUNT_DECIMALS}})?$`);

// Whether a stored amount is one the service accepts: a plain number with '.', within the limits above.
// Empty clears the value and is always accepted.
export const isValidAmount = (value: string | null): boolean => value === null || value === '' || AMOUNT.test(value);

// One number format per locale, built on first use: building one costs far more than formatting with it.
const amountFormats = new Map<string, Intl.NumberFormat>();
const amountFormatFor = (locale: string | null | undefined): Intl.NumberFormat => {
	const key = locale || 'en-US';
	let format = amountFormats.get(key);
	if (!format) {
		const options = {minimumFractionDigits: AMOUNT_DECIMALS, maximumFractionDigits: AMOUNT_DECIMALS};
		try {
			// en-US comes after the locale, so a locale the browser does not know reads as en-US.
			format = new Intl.NumberFormat([key, 'en-US'], options);
		} catch {
			format = new Intl.NumberFormat('en-US', options);
		}
		amountFormats.set(key, format);
	}
	return format;
};

// An amount as Forecast writes money: the currency's symbol before or after the number with a space, and
// exactly AMOUNT_DECIMALS decimals in the separators and grouping of the viewer's locale (react-intl's
// locale), as intl.formatNumber writes Forecast's other money: '$ 1,250.00' and '1,250.00 €' in en-US,
// '1.250,00 kr.' in da, '1 250,00 €' in fr. A missing or unknown locale uses en-US. An amount that is not
// set reads as empty, one that does not parse reads as stored, and one with no currency reads as the
// number alone.
export const formatCustomFieldAmount = (
	value: string | null | undefined,
	currencyCode: string | null | undefined,
	locale: string | null | undefined
): string => {
	if (value === null || value === undefined || value === '') return '';
	if (!/^-?\d+(\.\d+)?$/.test(value)) return value;
	const amount = Number(value);
	const {symbol, prefixed} = currencyEntry(currencyCode);
	const digits = amountFormatFor(locale).format(Math.abs(amount));
	const sign = amount < 0 ? '-' : '';
	if (!symbol) return `${sign}${digits}`;
	return prefixed ? `${sign}${symbol} ${digits}` : `${sign}${digits} ${symbol}`;
};

// A stored amount as the number the amount input edits, or NaN when it is not set or is not an amount the
// service accepts (such as one with a third decimal, written by another client). The input then shows it
// empty and never rounds and re-saves it; only an amount the user types is saved. A valid stored amount has at
// most 11 significant digits (9 before the point, 2 after), which a JavaScript number holds exactly.
export const storedAmount = (value: string | null | undefined): number =>
	value != null && value !== '' && isValidAmount(value) ? Number(value) : NaN;

// What to store for an amount the input reports: a plain '.' decimal with exactly AMOUNT_DECIMALS (2) decimals
// and no symbol, grouping or exponent. The input has already rounded the amount to 2 decimals, and a number of
// at most 11 significant digits prints back exactly.
export const amountToStored = (amount: number): string => {
	const fixed = amount.toFixed(AMOUNT_DECIMALS);
	// toFixed keeps the minus of an amount that rounds to zero ("-0.00"); zero is never negative.
	return /^-0(\.0+)?$/.test(fixed) ? fixed.slice(1) : fixed;
};

// Whether two amounts the input reports are the same; two empty amounts (NaN) are.
export const sameAmount = (a: number, b: number): boolean => a === b || (Number.isNaN(a) && Number.isNaN(b));

// Stored decimals always use '.'; a user may type the locale separator.
export const normaliseDecimal = (val: string): string => val.replace(',', '.');

// Accepts both '.' and ',' as decimal separators (normalises before testing)
export const isValidDecimal = (val: string | null): boolean => {
	if (val === null || val === '') return true;
	return /^-?\d+(\.\d+)?$/.test(normaliseDecimal(String(val)));
};

// Returns a converterValue function that accepts the locale decimal separator.
// Stored values always arrive with '.'; user-typed values use decSep.
export const makeDecimalConverter = () => (value: string | number | null) => {
	if (value === null || value === undefined || value === '') {
		return {float: null, full: '', error: false};
	}
	const str = String(value).trim();
	const normalized = normaliseDecimal(str);
	if (/^-?\d+(\.\d+)?$/.test(normalized)) {
		return {float: parseFloat(normalized), full: str, error: false};
	}
	return {float: null, full: str, error: true};
};

// Returns a filterInput function that only allows the locale decimal separator.
export const makeDecimalFilter =
	(decSep: string) =>
	(val: string): string => {
		const allowPattern = decSep === ',' ? /[^0-9,-]/g : /[^0-9.-]/g;
		const dupPattern = decSep === ',' ? /,/g : /\./g;
		let result = val.replace(allowPattern, '');
		const isNegative = result.startsWith('-');
		result = result.replace(/-/g, '');
		if (isNegative) result = '-' + result;
		const sepIdx = result.indexOf(decSep);
		if (sepIdx !== -1) {
			result = result.slice(0, sepIdx + 1) + result.slice(sepIdx + 1).replace(dupPattern, '');
		}
		return result;
	};

// The service stores ISO dates only; the record page parses strictly with the same format.
export const isValidIsoDate = (val: string | null): boolean => {
	if (val === null || val === '') return true;
	return moment(val, 'YYYY-MM-DD', true).isValid();
};

// A dropdown value is stored as the option's numeric id, while GraphQL hands the option out with its
// global id ("CustomFieldDropdownOption:<id>", base64). Same decode the record page's ComboBox uses.
export const dropdownOptionKey = (optionGlobalId: string): string => atob(optionGlobalId).split(':')[1];

// A Multi-select value is a JSON array of the chosen options' numeric ids, each a string: '["12","15"]'. An
// empty value, or one that is not such an array, reads as nothing chosen.
export const parseMultiSelectValue = (value: string | null | undefined): string[] => {
	if (!value) return [];
	let parsed: unknown;
	try {
		parsed = JSON.parse(value);
	} catch {
		return [];
	}
	return Array.isArray(parsed) && parsed.every(id => typeof id === 'string') ? parsed : [];
};

// What to store for the chosen option ids: the JSON array the service reads, or null to clear the value when
// nothing is chosen. The service keeps the ids in the order of the option list, whatever order they come in.
export const toMultiSelectValue = (ids: ReadonlyArray<string>): string | null => (ids.length ? JSON.stringify(ids) : null);

export type MultiSelectOption = {id: string; displayName: string};

// The names of the chosen options, in the order of the option list. The options carry GraphQL global ids,
// decoded as a Dropdown's are. A stored id with no matching option, such as one deleted since, is skipped.
export const multiSelectNames = (
	value: string | null | undefined,
	options: ReadonlyArray<MultiSelectOption | null | undefined>
): string[] => {
	const chosen = new Set(parseMultiSelectValue(value));
	if (!chosen.size) return [];
	return options
		.filter((option): option is MultiSelectOption => !!option && chosen.has(dropdownOptionKey(option.id)))
		.map(option => option.displayName);
};

// Whether two selections hold the same option ids, in any order.
export const sameSelection = (a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean => {
	const left = new Set(a);
	const right = new Set(b);
	return left.size === right.size && [...left].every(id => right.has(id));
};

// A Yes/No field stores one of these. An empty value means it has not been set, and that reads as No
// everywhere: a Boolean cannot tell No from never answered (a Dropdown field can).
export const BOOLEAN_TRUE = 'true';
export const BOOLEAN_FALSE = 'false';

// The message that shows a Yes/No value as text: yes for 'true', and no for anything else ('false', empty,
// null, undefined). Every place that writes a Yes/No value as text uses this, so a cell and an export
// cannot disagree.
export const booleanLabelId = (value: string | null | undefined): 'common.yes' | 'common.no' =>
	value === BOOLEAN_TRUE ? 'common.yes' : 'common.no';

// What a surface knows about a value beyond its type, for the types that read differently because of it. The
// locale is react-intl's, which an amount is written in (formatCustomFieldAmount).
export type CustomFieldTextOptions = {currencyCode?: string | null; locale?: string | null};

// The text a stored value reads as wherever a custom field is shown as text (cells, side panels, report
// rows): a Yes/No field as the viewer's Yes or No, No when it is not set; an amount formatted in its
// currency; and every other type as stored. A value that is not set reads `empty` (a Yes/No reads No).
// A surface that writes dates or numbers in its own format does that first and uses this for the rest.
export const customFieldValueText = (
	type: string | null | undefined,
	value: string | null | undefined,
	formatMessage: (descriptor: {id: string}) => string,
	empty = '',
	options: CustomFieldTextOptions = {}
): string => {
	if (type === 'BOOLEAN') return formatMessage({id: booleanLabelId(value)});
	if (type === 'CURRENCY') return formatCustomFieldAmount(value, options.currencyCode, options.locale) || empty;
	return value || empty;
};

// The text a value reads as where its node also carries the service's display value (the Insights task and
// reported-time rows, and their CSV exports): a Dropdown or Multi-select as its option names, which only
// displayValue carries ("Design, Backend, QA"), and `empty` without one, never the stored ids; every other type
// as customFieldValueText reads it.
export const customFieldDisplayText = (
	customField: {type?: string | null; value?: string | null; displayValue?: string | null},
	formatMessage: (descriptor: {id: string}) => string,
	empty = '',
	options: CustomFieldTextOptions = {}
): string =>
	customField.type === 'DROPDOWN' || customField.type === 'MULTI_SELECT'
		? customField.displayValue || empty
		: customFieldValueText(customField.type, customField.value, formatMessage, empty, options);

// The longest link, in characters. The service refuses a longer link with the same limit.
export const HYPERLINK_MAX_LENGTH = 1000;

// Input that already names a scheme: either scheme:// or one of the schemes that take no //. Anything
// else, including a host with a port such as example.com:8080, gets https:// added. A scheme matched
// here and not allowed by isValidHyperlink is refused, never rewritten.
const HAS_SCHEME = /^([a-z][a-z0-9+.-]*:\/\/|(javascript|data|vbscript|mailto|tel):)/i;

// Whitespace, control and invisible format characters (such as U+202E right-to-left override or U+200B
// zero-width space), and a backslash, which browsers read as a slash: https://evil.example\@google.com
// opens evil.example. new URL strips some of these silently and accepts the rest, so a link could read as
// one site and open another.
const FORBIDDEN_LINK_CHARS = /[\s\p{Cc}\p{Cf}\\]/u;

// Scripts whose letters look alike. A host label that mixes them, such as "аpple" with a Cyrillic а, can
// read as a different site. A label in one script, such as "münchen" or "пример", is accepted.
const CONFUSABLE_SCRIPTS = [/\p{Script=Latin}/u, /\p{Script=Cyrillic}/u, /\p{Script=Greek}/u];
const mixesScripts = (label: string): boolean => CONFUSABLE_SCRIPTS.filter(script => script.test(label)).length > 1;

// Why a link cannot be saved, each with its own message (HYPERLINK_PROBLEM_MESSAGE_ID).
export type HyperlinkProblem = 'tooLong' | 'characters' | 'scheme' | 'host' | 'credentials' | 'lookalike';

export const HYPERLINK_PROBLEM_MESSAGE_ID: Record<HyperlinkProblem, string> = {
	tooLong: 'custom_field_value.hyperlink_too_long',
	characters: 'custom_field_value.hyperlink_characters',
	scheme: 'custom_field_value.hyperlink_invalid',
	host: 'custom_field_value.hyperlink_host',
	credentials: 'custom_field_value.hyperlink_credentials',
	lookalike: 'custom_field_value.hyperlink_lookalike',
};

// Why a stored link would be refused, or null when it is valid: an absolute http or https URL to a host
// containing a dot (an IP address such as 1.2.3.4 is accepted), within the stored size, that reads as the
// site it opens. The service applies the same rules, so a link that passes here also saves.
export const hyperlinkProblem = (val: string): HyperlinkProblem | null => {
	// Counted in code points, as MySQL counts characters.
	if ([...val].length > HYPERLINK_MAX_LENGTH) return 'tooLong';
	if (FORBIDDEN_LINK_CHARS.test(val)) return 'characters';
	// The scheme must be written out in full: new URL also accepts https:evil.com.
	if (!/^https?:\/\//i.test(val)) return 'scheme';
	// The host must follow the //: new URL also accepts https:///evil.com.
	if (!/^https?:\/\/[^/]/i.test(val)) return 'host';
	// The authority as written, from after :// to the path, query or fragment. Any @ in it is user info,
	// even an empty one: in https://google.com@evil.example the real host is evil.example, and
	// https://:@evil.example parses with no user name or password at all.
	const authority = val.slice(val.indexOf('//') + 2).split(/[/?#]/)[0];
	if (authority.includes('@')) return 'credentials';
	let url: URL;
	try {
		url = new URL(val);
	} catch {
		return 'host';
	}
	if (!/^[^.]+(\.[^.]+)+\.?$/.test(url.hostname)) return 'host';
	// The host as written, not as parsed: the parser turns a non-ASCII host into xn-- form, and the user
	// reads what was written.
	const writtenHost = authority.split(':')[0];
	return writtenHost.split('.').some(mixesScripts) ? 'lookalike' : null;
};

export const isValidHyperlink = (val: string): boolean => hyperlinkProblem(val) === null;

export type HyperlinkInputResult = {valid: true; value: string | null} | {valid: false; reason: HyperlinkProblem};

// What to save for what the user typed: null to clear it, the link (with https:// added when the user
// left the scheme out), or why it cannot be saved. Any other scheme, javascript: included, is invalid
// rather than rewritten, so the user sees their input refused instead of a different link saved.
export const normalizeHyperlink = (input: string): HyperlinkInputResult => {
	const trimmed = input.trim();
	if (trimmed === '') return {valid: true, value: null};
	// A protocol-relative "//host" would otherwise become "https:////host".
	const candidate = HAS_SCHEME.test(trimmed) ? trimmed : `https://${trimmed.replace(/^\/+/, '')}`;
	const problem = hyperlinkProblem(candidate);
	return problem ? {valid: false, reason: problem} : {valid: true, value: candidate};
};

// The href to render for a stored value, or null to show it as plain text. Every link is rendered through
// this, so a value written by another client can never become a javascript: link.
export const hyperlinkHref = (stored: string | null | undefined): string | null =>
	stored && isValidHyperlink(stored) ? stored : null;
